import { useEffect } from "react";
import { SITE } from "../config/site";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function clearManaged(selector) {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

/**
 * SEO head manager.
 * props:
 *  - title, description
 *  - path: current localized in-app path (e.g. "/fr/soins-dentaires-vietnam")
 *  - alternates: { en, fr, de } in-app paths for hreflang
 *  - lang: current language code
 *  - image: OG image url
 *  - type: "website" | "article"
 *  - jsonLd: object | array of objects
 *  - noindex: boolean
 */
export default function Seo({
  title,
  description,
  path = "/",
  alternates,
  lang = "en",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullUrl = `${SITE.domain}${path === "/" ? "" : path}`;
  const ogImage =
    image ||
    "https://images.unsplash.com/photo-1777331903190-341a3dd0441b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

  useEffect(() => {
    if (title) document.title = title;
    document.documentElement.setAttribute("lang", lang);

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullUrl);

    // hreflang alternates
    clearManaged('link[data-managed="hreflang"]');
    if (alternates) {
      const langMap = { en: "en", fr: "fr", de: "de" };
      Object.entries(alternates).forEach(([l, p]) => {
        if (!p) return;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", langMap[l] || l);
        link.setAttribute("href", `${SITE.domain}${p === "/" ? "" : p}`);
        link.setAttribute("data-managed", "hreflang");
        document.head.appendChild(link);
      });
      // x-default -> English
      if (alternates.en) {
        const xd = document.createElement("link");
        xd.setAttribute("rel", "alternate");
        xd.setAttribute("hreflang", "x-default");
        xd.setAttribute(
          "href",
          `${SITE.domain}${alternates.en === "/" ? "" : alternates.en}`
        );
        xd.setAttribute("data-managed", "hreflang");
        document.head.appendChild(xd);
      }
    }

    // Open Graph + Twitter
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", fullUrl);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:site_name", SITE.brand);
    upsertMeta("property", "og:locale", lang === "fr" ? "fr_FR" : lang === "de" ? "de_DE" : "en_GB");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    // JSON-LD
    clearManaged('script[data-managed="jsonld"]');
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj) => {
        if (!obj) return;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-managed", "jsonld");
        s.textContent = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, fullUrl, lang, type, ogImage, noindex, JSON.stringify(alternates), JSON.stringify(jsonLd)]);

  return null;
}
